import { useState, useRef, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { ScrollArea } from "./ui/scroll-area";
import { 
  MessageCircle, 
  X, 
  Send, 
  Bot, 
  User,
  Loader2,
  Sparkles,
  PiggyBank,
  CreditCard,
  Target,
  HelpCircle,
  Users,
  TrendingUp
} from "lucide-react";

// Suggested questions for quick access
const suggestedQuestions = [
  { text: "How do I set a budget?", icon: PiggyBank },
  { text: "What are the pricing plans?", icon: CreditCard },
  { text: "How do I connect my bank?", icon: CreditCard },
  { text: "How do I create a financial goal?", icon: Target },
  { text: "How does household sharing work?", icon: Users },
  { text: "What is cash flow forecasting?", icon: TrendingUp },
];

export const DocsChatbot = () => {
  const { api } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hi! I'm Tally's AI assistant. Ask me anything about using Tally, or tap a question below to get started!"
    }
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const sendMessage = async (messageText) => {
    if (!messageText.trim() || isLoading) return;

    // Hide suggestions after first user message
    setShowSuggestions(false);

    const userMessage = { role: "user", content: messageText.trim() };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput("");
    setIsLoading(true);

    try {
      const response = await api.post("/chatbot", {
        messages: newMessages.slice(1) // Exclude initial greeting
      });
      
      setMessages([...newMessages, {
        role: "assistant",
        content: response.data.response
      }]);
    } catch (error) {
      console.error("Chatbot error:", error);
      setMessages([...newMessages, {
        role: "assistant",
        content: "Sorry, I encountered an error. Please try again or contact support@usetally.ca for help."
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = () => {
    sendMessage(input);
  };

  const handleSuggestedQuestion = (question) => {
    sendMessage(question);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Chat Button */}
      <Button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-20 lg:bottom-6 right-4 lg:right-6 z-50 rounded-full w-14 h-14 shadow-lg hover:shadow-xl transition-all ${isOpen ? 'scale-0' : 'scale-100'}`}
        data-testid="chatbot-trigger"
      >
        <MessageCircle className="w-6 h-6" />
      </Button>

      {/* Chat Window */}
      <div className={`fixed bottom-20 lg:bottom-6 right-4 lg:right-6 z-50 transition-all duration-300 ${isOpen ? 'scale-100 opacity-100' : 'scale-95 opacity-0 pointer-events-none'}`}>
        <Card className="w-[350px] sm:w-[400px] shadow-2xl border-border/50 overflow-hidden">
          {/* Header */}
          <CardHeader className="bg-primary text-primary-foreground p-4 flex flex-row items-center justify-between space-y-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold">Tally Assistant</CardTitle>
                <p className="text-xs text-primary-foreground/80">Powered by AI</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(false)}
              className="h-8 w-8 text-primary-foreground hover:bg-white/20 rounded-full"
              data-testid="chatbot-close"
            >
              <X className="w-5 h-5" />
            </Button>
          </CardHeader>

          {/* Messages */}
          <CardContent className="p-0">
            <ScrollArea className="h-[350px] p-4">
              <div className="space-y-4">
                {messages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex gap-3 ${message.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    {message.role === "assistant" && (
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Bot className="w-4 h-4 text-primary" />
                      </div>
                    )}
                    <div
                      className={`rounded-2xl px-4 py-2 max-w-[80%] ${
                        message.role === "user"
                          ? "bg-primary text-primary-foreground rounded-br-md"
                          : "bg-muted rounded-bl-md"
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    </div>
                    {message.role === "user" && (
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-primary" />
                      </div>
                    )}
                  </div>
                ))}

                {/* Suggested Questions - shown after initial greeting */}
                {showSuggestions && messages.length === 1 && !isLoading && (
                  <div className="space-y-2 mt-4">
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <HelpCircle className="w-3 h-3" />
                      Quick questions:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {suggestedQuestions.map((q, idx) => {
                        const Icon = q.icon;
                        return (
                          <button
                            key={idx}
                            onClick={() => handleSuggestedQuestion(q.text)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-primary/5 hover:bg-primary/10 text-primary border border-primary/20 rounded-full transition-colors"
                            data-testid={`suggested-q-${idx}`}
                          >
                            <Icon className="w-3 h-3" />
                            {q.text}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {isLoading && (
                  <div className="flex gap-3 justify-start">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4 text-primary" />
                    </div>
                    <div className="rounded-2xl rounded-bl-md bg-muted px-4 py-2">
                      <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {/* Input */}
            <div className="p-4 border-t border-border/50">
              <div className="flex gap-2">
                <Input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your question..."
                  className="flex-1 rounded-full"
                  disabled={isLoading}
                  data-testid="chatbot-input"
                />
                <Button
                  onClick={handleSend}
                  disabled={!input.trim() || isLoading}
                  size="icon"
                  className="rounded-full h-10 w-10"
                  data-testid="chatbot-send"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground text-center mt-2">
                AI responses are based on Tally documentation
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default DocsChatbot;
